import { trackCotacaoApresentacao, trackCotacaoStepView } from "@/analytics/tracking";
import { TestimonialSection } from "@/components/sections/section-testimonial";
import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/ui/google-icon";
import ImageSvg from "@/components/ui/image-svg";
import { useQuoteContext } from "@/contexts/quote-context";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { BiTargetLock } from "react-icons/bi";
import { FaCheckCircle, FaUsers } from "react-icons/fa";
import { LuMapPin } from "react-icons/lu";
import { TbMapPins, TbTruckDelivery } from "react-icons/tb";
import CardContactSpecialist from "../card-contact-specialist";
import CardPriceNew from "../card-price-new";
import CotacaoNotFound from "../cotacaoNotFound";
import QuoteCardResume from "../quote-card-resume";
import QuoteCardResumeMobile from "../quote-card-resume-mobile";
import QuoteSummarySkeleton from "../quote-summary-skeleton";

interface QuoteSummaryProps {
  onNext: () => void;
  isLoading?: boolean;
}
export default function QuoteSummary({
  onNext,
  isLoading = false,
}: QuoteSummaryProps) {
  const { cotacao, quotePrice, errorCotacao } = useQuoteContext();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [isOrderSummaryExpanded, setIsOrderSummaryExpanded] = useState(false);

  useEffect(() => {
    trackCotacaoStepView("cotacao");
  }, []);

  useEffect(() => {
    if (cotacao?.cve_codigo) {
      trackCotacaoApresentacao(cotacao);
    }
  }, [cotacao]);

  if (isLoading || !cotacao) return <QuoteSummarySkeleton />;
  if (errorCotacao && !cotacao) return <CotacaoNotFound />;

  if (isMobile) {
    return (
      <div className="mb-[50px]">
        <div className="py-2">
          <h2 className="text-2xl font-semibold text-n-green mb-3">
            Olá {cotacao.ctc_contato?.split(" ")[0] || "Cliente"}, sua cotação:
          </h2>

          <div className="flex gap-2 mt-3 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex items-center bg-white rounded-full px-4 py-2 text-sm font-medium shadow-sm whitespace-nowrap flex-shrink-0">
              <span className="text-n-green font-semibold mr-2">Origem:</span>
              <BiTargetLock className="text-n-text-neutral mr-1" />
              <span className="text-n-text-neutral">
                {cotacao.municipio_base_origem || "Não informado"}
              </span>
            </div>
            <div className="flex items-center bg-white rounded-full px-4 py-2 text-sm font-medium shadow-sm whitespace-nowrap flex-shrink-0">
              <span className="text-n-green font-semibold mr-2">Destino:</span>
              <LuMapPin className="text-n-text-neutral mr-1" />
              <span className="text-n-text-neutral">
                {cotacao.municipio_base_destino || "Não informado"}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <CardPriceNew onNext={onNext} quotePrice={quotePrice} />
        </div>

        <div className="bg-white shadow-md rounded-[20px] py-4 px-4 mt-4">
          <h3 className="font-semibold text-n-text-green text-base mb-3">
            Seu carro em boas mãos:
          </h3>
          <div className="space-y-3 text-xs">
            <div className="flex items-center gap-3 border-b pb-2">
              <ImageSvg
                src="reclameaqui.svg"
                alt="Reclame Aqui"
                className="object-contain w-5 h-full"
              />
              <span className="text-n-text-neutral font-medium">
                Somos nota máxima no Reclame Aqui
              </span>
            </div>
            <div className="flex items-center gap-3 border-b pb-2">
              <div className="w-5 h-5 flex-shrink-0">
                <GoogleIcon />
              </div>
              <span className="text-n-text-neutral font-medium">
                ★ 5 Estrelas no Google
              </span>
            </div>
            <div className="flex items-center gap-3 border-b pb-2">
              <FaUsers className="h-6 w-6 text-n-green flex-shrink-0" />
              <span className="text-n-text-neutral font-medium">
                + de 750 mil clientes atendidos
              </span>
            </div>
            <div className="flex items-center gap-3 border-b pb-2">
              <TbTruckDelivery className="h-6 w-6 text-n-green flex-shrink-0" />
              <span className="text-n-text-neutral font-medium">
                + 200 mil veículos transportados
              </span>
            </div>
            <div className="flex items-center gap-3 border-b pb-2">
              <TbMapPins className="h-6 w-6 text-n-green flex-shrink-0" />
              <span className="text-n-text-neutral font-medium">
                + 200 bases pelo Brasil
              </span>
            </div>
            <div className="flex items-center gap-3">
              <FaCheckCircle className="h-6 w-6 text-n-green flex-shrink-0" />
              <span className="text-n-text-neutral font-medium">
                + 10 mil transportadores credenciados
              </span>
            </div>
          </div>
        </div>

        <div className="fixed min-h-[80px] bottom-0 left-0 right-0 rounded-t-2xl bg-white border-t z-50">
          <div
            className="p-4 cursor-pointer"
            onClick={() => setIsOrderSummaryExpanded(!isOrderSummaryExpanded)}
          >
            <div className="flex items-center justify-between pb-3 border-b">
              <p className="text-lg text-n-text-green font-bold">Resumo:</p>
              {isOrderSummaryExpanded ? (
                <ChevronDown className="h-5 w-5 text-n-green" />
              ) : (
                <ChevronUp className="h-5 w-5 text-n-green" />
              )}
            </div>

            <div className="flex items-center justify-between mt-2">
              <div>
                <p className="text-sm text-n-text">Total</p>
                <p className="text-xl font-bold text-n-green">
                  R${" "}
                  {quotePrice.total.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                  }}
                >
                  Personalizar
                  <ArrowRight className="ml-2 " />
                </Button>
              </div>
            </div>
          </div>

          {isOrderSummaryExpanded && (
            <div className="border-t bg-gray-50 max-h-96 overflow-y-auto">
              <QuoteCardResumeMobile />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="pt-6">
        <div className="flex items-center gap-8">
          <h2 className="text-3xl font-semibold text-n-green">
            Olá {cotacao.ctc_contato?.split(" ")[0] || "Cliente"}, sua cotação:
          </h2>

          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center  bg-white rounded-full px-4 py-2 text-sm font-medium">
              <span className=" text-n-green font-semibold mr-2">Origem:</span>
              <BiTargetLock className="text-n-text-neutral mr-1" />
              <span className="text-n-text-neutral font-medium">
                {cotacao.municipio_base_origem || "Não informado"}
              </span>
            </div>
            <div className="flex items-center bg-white rounded-full px-4 py-2 text-sm font-medium">
              <span className=" text-n-green font-semibold mr-2">Destino:</span>
              <LuMapPin className="text-n-text-neutral mr-1" />
              <span className="text-n-text-neutral font-medium">
                {cotacao.municipio_base_destino || "Não informado"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-9 gap-5 py-5">
        <div className="col-span-2">
          <div className="flex bg-white  rounded-[20px] py-4 px-6 flex-col">
            <h3 className="font-semibold text-n-text-green text-lg mb-4">
              Seu carro em boas mãos:
            </h3>
            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-3 border-b pb-2">
                <ImageSvg
                  src="reclameaqui.svg"
                  alt="Reclame Aqui"
                  className="object-contain w-5 h-full"
                />
                <span className="text-n-text-neutral font-medium">
                  Somos nota máxima no Reclame Aqui
                </span>
              </div>
              <div className="flex items-center gap-3 border-b pb-2">
                <div className="w-5 h-5 flex-shrink-0">
                  <GoogleIcon />
                </div>
                <span className="text-n-text-neutral font-medium">
                  ★ 5 Estrelas no Google
                </span>
              </div>
              <div className="flex items-center gap-3 border-b pb-2">
                <FaUsers className="h-6 w-6 text-n-green flex-shrink-0" />
                <span className="text-n-text-neutral font-medium">
                  + de 750 mil clientes atendidos
                </span>
              </div>
              <div className="flex items-center gap-3 border-b pb-2">
                <TbTruckDelivery className="h-6 w-6 text-n-green flex-shrink-0" />
                <span className="text-n-text-neutral font-medium">
                  + 200 mil veículos transportados
                </span>
              </div>
              <div className="flex items-center gap-3 border-b pb-2">
                <TbMapPins className="h-6 w-6 text-n-green flex-shrink-0" />
                <span className="text-n-text-neutral font-medium">
                  + 200 bases pelo Brasil
                </span>
              </div>
              <div className="flex items-center gap-3">
                <FaCheckCircle className="h-6 w-6 text-n-green flex-shrink-0" />
                <span className="text-n-text-neutral font-medium">
                  + 10 mil transportadores credenciados
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-4">
          <CardPriceNew onNext={onNext} quotePrice={quotePrice} />
        </div>

        <div className="col-span-3">
          <CardContactSpecialist />
          <div className="mt-5">
            <QuoteCardResume />
          </div>
        </div>
      </div>
      <div>
        <TestimonialSection />
      </div>
    </div>
  );
}
